import { plainToClass } from "class-transformer";
import { Appendix } from "../../../../../../shared/entities/scripts/appendix";
import { Study } from "../../../../../../shared/entities/scripts/study";
import { FilterOption } from "../../../../../../shared/scripts/filtering/filtering.entities";
import { SetActiveTab } from "../../../../../../shared/scripts/tab-utilities";
import { SiteInfo } from "../../../../sites/shared/sites.entities";
import { ISiteInfo } from "../../../scripts/data-completeness.page";
import { AssessmentsDatatableView } from "../assessments-datatable-view/assessments-datatable-view";
import { AssessmentsDatatableEntry } from "../assessments-datatable-view/assessments-datatable.entities";
import { AssessmentsSummaryView } from "../assessments-summary-view/assessments-summary-view";
import { AssessmentSummarySeries } from "../assessments-summary-view/assessments-summary.entities";
import { Assessment, AssessmentEvent } from "./assessments.entities";
import { AssessmentLookup } from "./assessments.lookup";
import { VisitLookup } from "../../visits-tab/scripts/visits.lookup";

export class AssessmentsController {

    siteInfo: ISiteInfo;

    allTimepoints: Assessment[] = [];
    allAssessments: Assessment[] = [];
    allAssessmentEvents: AssessmentEvent[] = [];
    // allVisitRequired: FilterOption[] = [];

    summaryView: AssessmentsSummaryView;
    datatableView: AssessmentsDatatableView;

    //selectedVisitEvents: VisitEvent[];

    constructor(siteInfo: ISiteInfo, assessmentsJson: any) {

        this.siteInfo = siteInfo;

        let foundAssessmentIds: number[] = [];
        let foundTimepointIds: number[] = [];
        // let foundVisitRequiredIds: number[] = [];
        this.allAssessmentEvents = plainToClass(AssessmentEvent, <AssessmentEvent[]>assessmentsJson);
        for (let i = 0; i < this.allAssessmentEvents.length; i++) {
            let v = this.allAssessmentEvents[i];

            v.initialize();

            if (!foundAssessmentIds.includes(v.NameId)) {
                this.allAssessments.push(new Assessment(v.NameId, AssessmentLookup.GetNameColumn(v.NameId), null));
                foundAssessmentIds.push(v.NameId);
            }

            if (!foundTimepointIds.includes(v.TimepointId)) {
                this.allTimepoints.push(new Assessment(v.TimepointId, VisitLookup.GetVisitMasterColumn(v.TimepointId), VisitLookup.GetVisitMasterOrdering(v.TimepointId)));
                foundTimepointIds.push(v.TimepointId);
            }
        }

        this.allAssessments.sort((a: Assessment, b: Assessment) => a.OrderBy - b.OrderBy);
        this.allTimepoints.sort((a: Assessment, b: Assessment) => a.OrderBy - b.OrderBy);
        // this.allVisitRequired.sort((a: FilterOption, b: FilterOption) => a.Id - b.Id);

        this.summaryView = new AssessmentsSummaryView();
        this.datatableView = new AssessmentsDatatableView();

        //console.log(this.allAssessments);
        //console.log(this.allAssessmentEvents);
    }

    FilterUpdated(studyIds: number[], appendixIds: number[], siteIds: number[], visitIds: number[], assessmentIds: number[], timepointIds: number[]): void {

        let byVisitSummaryMap: Map<number, AssessmentSummarySeries> = new Map<number, AssessmentSummarySeries>();
        let bySiteSummaryMap: Map<number, AssessmentSummarySeries> = new Map<number, AssessmentSummarySeries>();
        let byAssessmentSummaryMap: Map<number, AssessmentSummarySeries> = new Map<number, AssessmentSummarySeries>();

        let noGroupingDataMap: Map<string, AssessmentsDatatableEntry> = new Map<string, AssessmentsDatatableEntry>();
        let groupingByVisitDataMap: Map<string, AssessmentsDatatableEntry> = new Map<string, AssessmentsDatatableEntry>();
        let groupingBySiteDataMap: Map<string, AssessmentsDatatableEntry> = new Map<string, AssessmentsDatatableEntry>();
        let groupingByAssessmentDataMap: Map<string, AssessmentsDatatableEntry> = new Map<string, AssessmentsDatatableEntry>();

        //for loop instead of forEach for performance given there could be tens of thousands of visit events across all trials
        for (let i = 0; i < this.allAssessmentEvents.length; ++i) {
            let visit: AssessmentEvent = this.allAssessmentEvents[i];

            if (!studyIds.includes(visit.StudyId) || 
                !appendixIds.includes(visit.AppendixId) ||
                !assessmentIds.includes(visit.NameId) ||
                !timepointIds.includes(visit.TimepointId) ||
                !siteIds.includes(visit.SiteNumber)) {
                continue;
            }

            //Summary View Data
            let visitSeries: AssessmentSummarySeries;
            if (byVisitSummaryMap.has(visit.TimepointId)) { visitSeries = byVisitSummaryMap.get(visit.TimepointId); }
            else { visitSeries = new AssessmentSummarySeries(VisitLookup.GetVisitMasterColumn(visit.TimepointId), "visit", visit.TimepointId); }
            visitSeries.addVisitEvent(visit);
            byVisitSummaryMap.set(visit.TimepointId, visitSeries);

            let assessmentSeries: AssessmentSummarySeries;
            if (byAssessmentSummaryMap.has(visit.NameId)) { assessmentSeries = byAssessmentSummaryMap.get(visit.NameId); }
            else { assessmentSeries = new AssessmentSummarySeries(AssessmentLookup.GetNameColumn(visit.NameId), "assessment", visit.NameId); }
            assessmentSeries.addVisitEvent(visit);
            byAssessmentSummaryMap.set(visit.NameId, assessmentSeries);

            let siteSeries: AssessmentSummarySeries;
            if (bySiteSummaryMap.has(visit.SiteNumber)) { siteSeries = bySiteSummaryMap.get(visit.SiteNumber); }
            else { siteSeries = new AssessmentSummarySeries(this.siteInfo.GetSiteName(visit.SiteNumber), "site", visit.SiteNumber); }
            siteSeries.addVisitEvent(visit);
            bySiteSummaryMap.set(visit.SiteNumber, siteSeries);
        
            //Datatable View Data
            let dataHash: string;

            let noGroupingData: AssessmentsDatatableEntry;
            dataHash = AssessmentsDatatableEntry.getHash("none", visit);
            if (noGroupingDataMap.has(dataHash)) { noGroupingData = noGroupingDataMap.get(dataHash); }
            else { noGroupingData = new AssessmentsDatatableEntry(Study.GetStudyName(visit.StudyId), Appendix.GetAppendixName(visit.AppendixId), this.siteInfo.GetSiteName(visit.SiteNumber), SiteInfo.FormatSiteNumber(visit.SiteNumber), VisitLookup.GetVisitMasterColumn(visit.TimepointId), AssessmentLookup.GetNameColumn(visit.NameId)); }
            noGroupingData.addVisitEvent(visit);
            noGroupingDataMap.set(dataHash, noGroupingData);

            let visitGroupingData: AssessmentsDatatableEntry;
            dataHash = AssessmentsDatatableEntry.getHash("by-visit", visit);
            if (groupingByVisitDataMap.has(dataHash)) { visitGroupingData = groupingByVisitDataMap.get(dataHash); }
            else { visitGroupingData = new AssessmentsDatatableEntry(Study.GetStudyName(visit.StudyId), Appendix.GetAppendixName(visit.AppendixId), "All Sites", "All Sites", VisitLookup.GetVisitMasterColumn(visit.TimepointId), "All Assessments"); }
            visitGroupingData.addVisitEvent(visit);
            groupingByVisitDataMap.set(dataHash, visitGroupingData);

            let siteGroupingData: AssessmentsDatatableEntry;
            dataHash = AssessmentsDatatableEntry.getHash("by-site", visit);
            if (groupingBySiteDataMap.has(dataHash)) { siteGroupingData = groupingBySiteDataMap.get(dataHash); }
            else { siteGroupingData = new AssessmentsDatatableEntry(Study.GetStudyName(visit.StudyId), Appendix.GetAppendixName(visit.AppendixId), this.siteInfo.GetSiteName(visit.SiteNumber), SiteInfo.FormatSiteNumber(visit.SiteNumber), "All Visits", "All Assessments"); }
            siteGroupingData.addVisitEvent(visit);
            groupingBySiteDataMap.set(dataHash, siteGroupingData);

            let assessmentGroupingData: AssessmentsDatatableEntry;
            dataHash = AssessmentsDatatableEntry.getHash("by-assessment", visit);
            if (groupingByAssessmentDataMap.has(dataHash)) { assessmentGroupingData = groupingByAssessmentDataMap.get(dataHash); }
            else { assessmentGroupingData = new AssessmentsDatatableEntry(Study.GetStudyName(visit.StudyId), Appendix.GetAppendixName(visit.AppendixId), "All Sites", "All Sites", "All Visits", AssessmentLookup.GetNameColumn(visit.NameId)); }
            assessmentGroupingData.addVisitEvent(visit);
            groupingByAssessmentDataMap.set(dataHash, assessmentGroupingData);
        }

        let seriesByVisit: AssessmentSummarySeries[] = [...byVisitSummaryMap.values()];
        seriesByVisit.sort((a: AssessmentSummarySeries, b: AssessmentSummarySeries) => a.sortValue - b.sortValue);              

        let seriesByAssessment: AssessmentSummarySeries[] = [...byAssessmentSummaryMap.values()];
        seriesByAssessment.sort((a: AssessmentSummarySeries, b: AssessmentSummarySeries) => a.sortValue - b.sortValue);

        let seriesBySite: AssessmentSummarySeries[] = [...bySiteSummaryMap.values()];
        seriesBySite.sort((a: AssessmentSummarySeries, b: AssessmentSummarySeries) => b.sortValue - a.sortValue);

        this.summaryView.UpdateData(seriesByVisit, seriesBySite, seriesByAssessment);

        let tableEntriesNone: AssessmentsDatatableEntry[] = [...noGroupingDataMap.values()];
        for (let i: number = 0; i < tableEntriesNone.length; ++i) { tableEntriesNone[i].initialize(); }
        //sort it somehow

        let tableEntriesByAssessment: AssessmentsDatatableEntry[] = [...groupingByAssessmentDataMap.values()];
        for (let i: number = 0; i < tableEntriesByAssessment.length; ++i) { tableEntriesByAssessment[i].initialize(); }

        let tableEntriesByVisit: AssessmentsDatatableEntry[] = [...groupingByVisitDataMap.values()];
        for (let i: number = 0; i < tableEntriesByVisit.length; ++i) { tableEntriesByVisit[i].initialize(); }

        let tableEntriesBySite: AssessmentsDatatableEntry[] = [...groupingBySiteDataMap.values()];
        for (let i: number = 0; i < tableEntriesBySite.length; ++i) { tableEntriesBySite[i].initialize(); }

        this.datatableView.UpdateData(tableEntriesNone, tableEntriesByAssessment, tableEntriesByVisit, tableEntriesBySite);
    }

    setView(newView: string): void {

        //console.log("visits.setView", newView);
        if (newView == "view-summary") { SetActiveTab("assessments-tabs", "assessments-summary-content"); this.summaryView.SetView("assessment-summary"); }
        else if (newView == "view-datatable") { SetActiveTab("assessments-tabs", "assessments-datatable-content"); this.datatableView.SetView("assessment-datatable"); }
        else {
            this.summaryView.SetView(newView);
            this.datatableView.SetView(newView);
        }
    }

   /* exportTable(): void {
        this.datatableView.exportTable();
    }*/
}
